const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8000";

async function request<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });
  if (!res.ok) {
    const error = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(error.detail || "Request failed");
  }
  return res.json();
}

export interface CriteriaSet {
  name?: string;
  description?: string;
  lead_type?: string;
  duration_days?: number;
  region?: string;
  leads_per_company?: number;
  client_touches?: number;
  delivery_method?: string;
  delivery_days?: string[];
  country?: string;
  department?: string;
  domain?: string;
  email?: string;
  employee_count?: string;
  install_base?: string;
  invalid_job_title?: string;
  job_function?: string[];
  job_sector?: string;
  job_title?: string;
  revenue?: string;
  seniority?: string[];
}

export interface DisplayConfig {
  format?: string[];
  placement?: string;
  impressions?: string;
  flight_start?: string;
  flight_end?: string;
  region?: string;
  notes?: string;
}

export interface ContentMarketingConfig {
  content_type?: string[];
  topic?: string;
  word_count?: string;
  distribution?: string[];
  region?: string;
  notes?: string;
}

export interface WebcastConfig {
  topic?: string;
  format?: string;
  expected_attendees?: string;
  duration_minutes?: number;
  preferred_date?: string;
  region?: string;
  notes?: string;
}

export interface IntakeData {
  audience_type: "agency" | "client";
  client_name: string;
  approach: "standard" | "innovation";
  innovation_notes?: string;
  products: string[];
  products_other?: string;
  criteria_set?: CriteriaSet;
  display_config?: DisplayConfig;
  content_marketing_config?: ContentMarketingConfig;
  webcast_config?: WebcastConfig;
}

export interface ChatResponse {
  session_id: string;
  reply: string;
  deck_brief: DeckBrief | null;
}

export interface DeckBrief {
  client: string;
  industry: string;
  products: string[];
  key_messages: string[];
  audience: string;
  tone: string;
  slide_count: number;
  must_include: string[];
}

export interface DeckGenerateResponse {
  deck_id: string;
  download_url: string | null;
  google_slides_url: string | null;
}

export interface FileUploadResponse {
  file_id: string;
  filename: string;
  content_type: string;
  slide_count: number | null;
}

export interface AssetResult {
  asset_id: string;
  file_name: string;
  asset_type: string;
  content_preview: string;
  relevance_score: number;
  source_path: string;
  slide_number: number | null;
}

export interface SlidePreview {
  slide_number: number;
  title: string;
  body: string;
}

export interface DeckPreviewData {
  deck_id: string;
  slide_count: number;
  slides: SlidePreview[];
}

export interface DeckRevisionResponse {
  deck_id: string;
  slide_count: number;
  slides: SlidePreview[];
  reply: string;
}

export interface DriveFile {
  id: string;
  name: string;
  mimeType: string;
  modifiedTime: string;
}

export interface FileSlidePreview {
  slide_number: number;
  title: string;
  body: string;
  thumbnail: string | null; // base64 PNG for PDF pages
}

export interface FileSlides {
  file_id: string;
  file_name: string;
  file_type: string;
  slides: FileSlidePreview[];
}

export interface ProjectSlide {
  file_id: string;
  file_name: string;
  slide_number: number;
  title: string;
  file_type: string;
  thumbnail: string | null;
}

export interface AssembleResponse {
  deck_id: string;
  download_url: string;
  slide_count: number;
}

export interface OutlineSlide {
  slide_number: number;
  title: string;
  synopsis: string;
  layout: string;
  source: "ai" | "selected";
  selected_slide_index: number | null;
  thumbnail: string | null;
}

export interface OutlineResponse {
  outline_id: string;
  title: string;
  slides: OutlineSlide[];
}

export interface LibrarySlide {
  file_id: string;
  file_name: string;
  slide_number: number;
  thumbnail_url: string;
}

export interface SlideRecommendation {
  file_id: string;
  file_name: string;
  slide_number: number;
  content_preview: string;
  relevance_score: number;
  thumbnail_url: string | null;
}

export interface HealthResponse {
  status: string;
  version: string;
}

export const api = {
  async health(): Promise<HealthResponse> {
    return request("/health");
  },

  async newSession(): Promise<{ session_id: string }> {
    return request("/api/chat/new-session", { method: "POST" });
  },

  async sendMessage(
    sessionId: string,
    message: string,
    uploadedFileId?: string,
    intakeData?: IntakeData
  ): Promise<ChatResponse> {
    return request("/api/chat/", {
      method: "POST",
      body: JSON.stringify({
        session_id: sessionId,
        message,
        uploaded_file_id: uploadedFileId,
        intake_data: intakeData,
      }),
    });
  },

  async generateDeck(
    sessionId: string,
    deckBrief: DeckBrief,
    outputFormat: string = "pptx",
    selectedSlides?: { file_id: string; file_name: string; slide_number: number }[]
  ): Promise<DeckGenerateResponse> {
    return request("/api/deck/generate", {
      method: "POST",
      body: JSON.stringify({
        session_id: sessionId,
        deck_brief: deckBrief,
        output_format: outputFormat,
        selected_slides: selectedSlides || [],
      }),
    });
  },

  async uploadFile(file: File): Promise<FileUploadResponse> {
    const formData = new FormData();
    formData.append("file", file);
    const res = await fetch(`${API_BASE}/api/assets/upload`, {
      method: "POST",
      body: formData,
    });
    if (!res.ok) throw new Error("Upload failed");
    return res.json();
  },

  async searchAssets(
    query: string,
    maxResults: number = 10
  ): Promise<{ results: AssetResult[] }> {
    return request("/api/assets/search", {
      method: "POST",
      body: JSON.stringify({ query, max_results: maxResults }),
    });
  },

  getDeckDownloadUrl(deckId: string): string {
    return `${API_BASE}/api/deck/download/${deckId}`;
  },

  async previewDeck(deckId: string): Promise<DeckPreviewData> {
    return request(`/api/deck/preview/${deckId}`);
  },

  async reviseDeck(
    sessionId: string,
    feedback: string
  ): Promise<DeckRevisionResponse> {
    return request("/api/deck/revise", {
      method: "POST",
      body: JSON.stringify({
        session_id: sessionId,
        feedback,
      }),
    });
  },

  async uploadToDrive(deckId: string): Promise<{ file_id: string; google_drive_url: string }> {
    return request(`/api/deck/upload-to-drive/${deckId}`, { method: "POST" });
  },

  async authStatus(): Promise<{ google_configured: boolean; google_authenticated: boolean }> {
    return request("/auth/status");
  },

  async listDriveFiles(): Promise<{ files: DriveFile[] }> {
    return request("/api/drive/files");
  },

  async syncDrive(): Promise<{ status: string; documents_indexed: number }> {
    return request("/api/drive/sync", { method: "POST" });
  },

  async generateOutline(
    sessionId: string,
    deckBrief: DeckBrief,
    selectedSlides?: { file_id: string; file_name: string; slide_number: number }[]
  ): Promise<OutlineResponse> {
    return request("/api/deck/outline", {
      method: "POST",
      body: JSON.stringify({
        session_id: sessionId,
        deck_brief: deckBrief,
        selected_slides: selectedSlides || [],
      }),
    });
  },

  async buildFromOutline(
    sessionId: string,
    outlineId: string,
    deckBrief: DeckBrief,
    approvedSlides: OutlineSlide[],
    selectedSlides?: { file_id: string; file_name: string; slide_number: number }[]
  ): Promise<DeckGenerateResponse> {
    return request("/api/deck/build-from-outline", {
      method: "POST",
      body: JSON.stringify({
        session_id: sessionId,
        outline_id: outlineId,
        deck_brief: deckBrief,
        slides: approvedSlides,
        selected_slides: selectedSlides || [],
      }),
    });
  },

  async listAllSlides(): Promise<{ slides: LibrarySlide[]; total: number }> {
    return request("/api/drive/slides");
  },

  async recommendSlides(
    sessionId: string,
    deckBrief: DeckBrief
  ): Promise<{ recommendations: SlideRecommendation[] }> {
    return request("/api/deck/recommend", {
      method: "POST",
      body: JSON.stringify({ session_id: sessionId, deck_brief: deckBrief }),
    });
  },

  getThumbnailUrl(fileId: string, slideNumber: number): string {
    return `${API_BASE}/api/drive/thumbnail/${fileId}/${slideNumber}`;
  },

  async getFileSlides(fileId: string): Promise<FileSlides> {
    return request(`/api/drive/files/${fileId}/slides`);
  },

  async assembleDeck(
    slides: { file_id: string; file_name: string; slide_number: number }[]
  ): Promise<AssembleResponse> {
    return request("/api/deck/assemble", {
      method: "POST",
      body: JSON.stringify({ slides }),
    });
  },

  getAuthLoginUrl(): string {
    return `${API_BASE}/auth/login`;
  },
};
